import React, { useEffect, useMemo, useState } from "react";
import { normalizeText, toBRDate } from "../core/cimInterdicoes.utils";
import styles from "./InterdicaoCcoCard.module.css";

function getStatusClassKey(status) {
  return `st_${normalizeText(status).replace(/\s+/g, "_") || "neutro"}`;
}

export default function InterdicaoCcoCard({ interdicao, onSave, onFinalize, saving = false }) {
  const [draftNumero, setDraftNumero] = useState(interdicao?.numeroInterdicao || "");
  const [draftSbInterdicao, setDraftSbInterdicao] = useState(interdicao?.sbInterdicao || "");

  useEffect(() => {
    setDraftNumero(interdicao?.numeroInterdicao || "");
    setDraftSbInterdicao(interdicao?.sbInterdicao || "");
  }, [interdicao?.numeroInterdicao, interdicao?.sbInterdicao]);

  const statusClass = styles[getStatusClassKey(interdicao?.status)] || "";
  const hasNumero = Boolean((interdicao?.numeroInterdicao || "").trim());
  const hasSbInterdicao = Boolean((interdicao?.sbInterdicao || "").trim());
  const observacao = String(interdicao?.observacao || "").trim();
  const canEditCco = String(interdicao?.status || "").trim() === "Aguardando CCO";
  const canFinalizeCco =
    String(interdicao?.status || "").trim() === "Aguardando Finalização CCO";

  const dirty =
    draftNumero.trim() !== (interdicao?.numeroInterdicao || "").trim() ||
    draftSbInterdicao.trim() !== (interdicao?.sbInterdicao || "").trim();

  const inicioLabel = useMemo(() => {
    const dateLabel = toBRDate(interdicao?.dataInicio);
    if (!dateLabel) return "Sem início informado";
    return `${dateLabel}${interdicao?.horaInicio ?` às ${interdicao.horaInicio}` : ""}`;
  }, [interdicao?.dataInicio, interdicao?.horaInicio]);

  const previsaoLabel = useMemo(() => {
    if (!interdicao?.temPrevisao || !interdicao?.dataFim) return "Sem previsão de término";
    return `${toBRDate(interdicao.dataFim)}${interdicao.horaFim ?` às ${interdicao.horaFim}` : ""}`;
  }, [interdicao?.dataFim, interdicao?.horaFim, interdicao?.temPrevisao]);

  const kmLabel = useMemo(() => {
    if (interdicao?.kmInicial && interdicao?.kmFinal) {
      return `${interdicao.kmInicial} até ${interdicao.kmFinal}`;
    }
    return interdicao?.kmInicial || interdicao?.kmFinal || "Não informado";
  }, [interdicao?.kmFinal, interdicao?.kmInicial]);

  const handleSave = () => {
    onSave?.(interdicao?.id, {
      numeroInterdicao: draftNumero.trim(),
      sbInterdicao: draftSbInterdicao.trim(),
    });
  };

  const handleFinalize = () => {
    onFinalize?.(interdicao?.id);
  };

  return (
    <article className={`${styles.card} ${statusClass}`}>
      <div className={styles.metaCol} aria-hidden>
        <div className={styles.pillBox}>
          <span className={`${styles.pill} ${styles.pillCode}`}>
            {interdicao?.codigoUnico || interdicao?.identificador || "Sem ID"}
          </span>
        </div>
        <div className={styles.pillBox}>
          <span className={`${styles.pill} ${styles.pillStatus}`}>
            {interdicao?.status || "Sem status"}
          </span>
        </div>
        <div className={styles.pillBox}>
          <span className={`${styles.pill} ${styles.pillNumero}`}>
            {hasNumero ?`Nº ${interdicao.numeroInterdicao}` : "Sem nº"}
          </span>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.hero}>
          <span className={styles.eyebrow}>Interdição operacional</span>
          <div className={styles.header}>
            <div className={styles.titleGroup}>
              <h3 className={styles.title}>{`SB ${interdicao?.sb || "-"}`}</h3>
              <p className={styles.subtitle}>
                {interdicao?.sub ?`SUB ${interdicao.sub}` : "SUB não informada"}
              </p>
            </div>
          </div>
        </div>

        <div className={styles.summaryGrid}>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>Início</span>
            <strong className={styles.summaryValue}>{inicioLabel}</strong>
          </div>

          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>Previsão</span>
            <strong className={styles.summaryValue}>{previsaoLabel}</strong>
          </div>

          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>Número atual</span>
            <strong
              className={`${styles.summaryValue} ${!hasNumero ?styles.summaryValuePending : ""}`}
            >
              {hasNumero ?`Nº ${interdicao.numeroInterdicao}` : "Pendente no CCO"}
            </strong>
          </div>

          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>SB da interdição</span>
            <strong
              className={`${styles.summaryValue} ${!hasSbInterdicao ?styles.summaryValuePending : ""}`}
            >
              {hasSbInterdicao ?interdicao.sbInterdicao : "Pendente no CCO"}
            </strong>
          </div>
        </div>

        <div className={styles.detailRow}>
          <div className={`${styles.detailCard} ${styles.detailWide}`}>
            <span className={styles.detailLabel}>KM de trabalho</span>
            <strong className={styles.detailValue}>{kmLabel}</strong>
          </div>

          <div className={styles.detailCard}>
            <span className={styles.detailLabel}>Acionamento</span>
            <strong className={styles.detailValue}>
              {interdicao?.acionamento || "Não informado"}
            </strong>
          </div>
        </div>

        <div className={styles.reasonCard}>
          <span className={styles.reasonLabel}>Motivo</span>
          <strong className={styles.reasonValue}>{interdicao?.motivo || "Não informado"}</strong>
          {observacao ?<p className={styles.reasonNote}>{observacao}</p> : null}
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.footerHead}>
          <strong className={styles.footerTitle}>Atualização CCO</strong>
          <span className={styles.footerText}>
            {canEditCco
              ?"Preencha o número operacional e a SB desta interdição."
              : canFinalizeCco
                ?"A interdição está aguardando a baixa final do CCO."
                : "Dados CCO já aprovados. Não é possível alterar os números."}
          </span>
        </div>

        <div className={styles.footerGrid}>
          <label className={styles.field}>
            <span>Número da interdição</span>
            <input
              type="text"
              inputMode="numeric"
              value={draftNumero}
              onChange={(event) => setDraftNumero(event.target.value)}
              placeholder="Digite o número"
              disabled={!canEditCco || saving}
            />
          </label>

          <label className={styles.field}>
            <span>SB da interdição</span>
            <input
              type="text"
              value={draftSbInterdicao}
              onChange={(event) => setDraftSbInterdicao(event.target.value)}
              placeholder="Digite a SB"
              disabled={!canEditCco || saving}
            />
          </label>
        </div>

        <button
          type="button"
          className={styles.saveBtn}
          onClick={canFinalizeCco ?handleFinalize : handleSave}
          disabled={canFinalizeCco ?saving : !canEditCco || !dirty || saving}
        >
          {saving
            ?canFinalizeCco
              ?"Finalizando..."
              : "Salvando..."
            : canFinalizeCco
              ?"Dar baixa no CCO"
              : hasNumero || hasSbInterdicao
                ?"Atualizar dados CCO"
                : "Salvar dados CCO"}
        </button>
      </div>
    </article>
  );
}
